/**
 * The content library lives on the device. On startup we load the cached bundle, then ask
 * the API for only what changed (`?since=version`). Works fully offline once fetched.
 */
import type { Eclipse, LunarEvent, Retrograde, SabbatDate, Searchable } from "@hexling/core";
import { searchItems } from "@hexling/core";
import type { ContentCategory, ContentItem } from "@hexling/schema";
import type { Api } from "../lib/api";
import type { LocalStore } from "../storage/types";

export interface AstroEvent {
  kind: string;
  at: string;
  data: Record<string, unknown>;
}

export interface ContentCache {
  version: string | null;
  items: ContentItem[];
  astro: AstroEvent[];
}

interface Bundle extends ContentCache {
  full: boolean;
}

const KEY = "content";
const keyOf = (i: { category: string; slug: string }) => `${i.category}:${i.slug}`;

export async function loadCachedContent(store: LocalStore): Promise<ContentCache | null> {
  return (await store.getMeta<ContentCache>(KEY)) ?? null;
}

/** Apply a bundle (full or delta) to the cache. Deleted items are removed. Pure. */
export function applyBundle(current: ContentCache | null, bundle: Bundle): ContentCache {
  if (bundle.full || !current) {
    return {
      version: bundle.version,
      items: bundle.items.filter((i) => !i.deleted_at),
      astro: bundle.astro,
    };
  }
  const byKey = new Map(current.items.map((i) => [keyOf(i), i]));
  for (const item of bundle.items) {
    if (item.deleted_at) byKey.delete(keyOf(item));
    else byKey.set(keyOf(item), item);
  }
  return {
    version: bundle.version ?? current.version,
    items: [...byKey.values()],
    astro: current.astro,
  };
}

/** Fetch updates and persist them. Returns the new cache, or the old one when nothing changed. */
export async function refreshContent(
  store: LocalStore,
  api: Api,
  current: ContentCache | null,
): Promise<ContentCache> {
  const since = current?.version ? `?since=${encodeURIComponent(current.version)}` : "";
  const res = await api.request<Bundle>("GET", `/api/content/bundle${since}`);
  if (res.status === 304 || (!res.data.full && res.data.items.length === 0 && current))
    return current!;
  const next = applyBundle(current, res.data);
  await store.setMeta(KEY, next);
  return next;
}

/** Fast lookups over the cached content. Built once per cache version. */
export class ContentIndex {
  private readonly byKey = new Map<string, ContentItem>();
  private readonly byCategory = new Map<string, ContentItem[]>();

  constructor(public readonly cache: ContentCache) {
    for (const item of cache.items) {
      this.byKey.set(keyOf(item), item);
      const list = this.byCategory.get(item.category) ?? [];
      list.push(item);
      this.byCategory.set(item.category, list);
    }
    for (const list of this.byCategory.values())
      list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }

  get(category: string, slug: string): ContentItem | undefined {
    return this.byKey.get(`${category}:${slug}`);
  }

  list(category: ContentCategory): ContentItem[] {
    return this.byCategory.get(category) ?? [];
  }

  count(category: ContentCategory): number {
    return this.list(category).length;
  }

  /** Fuzzy search by name, other names and intents across categories. */
  search(query: string, categories?: ContentCategory[], limit = 40): ContentItem[] {
    const pool = categories ? categories.flatMap((c) => this.list(c)) : this.cache.items;
    const searchable = pool.map(
      (item) => Object.assign({}, item, { aliases: aliasesOf(item) }) as ContentItem & Searchable,
    );
    return searchItems(searchable, query, limit);
  }

  private astro<T>(kind: string): T[] {
    return this.cache.astro.filter((a) => a.kind === kind).map((a) => a.data as T);
  }

  get lunar(): LunarEvent[] {
    return this.astro<LunarEvent>("lunar");
  }
  get sabbatDates(): SabbatDate[] {
    return this.astro<SabbatDate>("sabbat");
  }
  get eclipses(): Eclipse[] {
    return this.astro<Eclipse>("eclipse");
  }
  get retrogrades(): Retrograde[] {
    return this.astro<Retrograde>("retrograde");
  }
}

function aliasesOf(item: ContentItem): string[] {
  const d = item.data;
  const arr = (k: string) =>
    Array.isArray(d[k])
      ? (d[k] as unknown[]).filter((x): x is string => typeof x === "string")
      : [];
  return [
    ...arr("other_names"),
    ...arr("intents").map((i) => i.replace(/_/g, " ")),
    ...arr("keywords_upright"),
  ];
}
